import { clearLocalStorage } from "helpers/localStorage";
import moment from "moment";
import { alert, responseMiddleware } from "./alert";

const { protocol, host } = location;
const url = `${protocol}//${host}`;
const requestsQueue: Array<() => void> = [];

export type ResponseWithStatus<T> = {
    data: T | false | undefined;
    status: number;
};

type RequestOptions = {
    noContentAlerts?: boolean;
    countInHeaders?: boolean;
    withoutMiddleware?: boolean;
    withoutConnectionAlert?: boolean;
    shouldAlert?: boolean;
    textBody?: boolean;
    withoutErrorAlert?: boolean;
    returnErrorText?: boolean;
    withStatus?: boolean;
    headers?: Record<string, string>;
    alertParams?: { successAlerts: boolean; noContentAlerts: boolean };
};

function options(headers: Record<string, string> = {}) {
    const token = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    return {
        method: "POST",
        cache: "default",
        headers: {
            "X-Requested-With": "XMLHttpRequest",
            "X-Device-Id": localStorage.getItem("deviceId"),
            "Content-Type": "application/json; charset=utf-8",
            ...token,
            ...headers,
        },
    };
}

export function connectionAlert(err: Error, withoutConnectionAlert = false) {
    if (withoutConnectionAlert) {
        throw err;
    }
    if (err.message === "Failed to fetch") {
        alert({
            type: "error",
            message: "Соединение с сервером потеряно. Попробуйте обновить страницу.",
            title: "Ошибка.",
        });
    } else {
        alert({ type: "error", message: "Неудачный запрос", title: "Ошибка." });
    }
    throw err;
}

export function refreshToken(withoutConnectionAlert = false) {
    const body = {
        AccessToken: localStorage.getItem("token"),
        RefreshToken: localStorage.getItem("refreshToken"),
    };
    return fetch(`${url}/api/account/RefreshToken`, { body: JSON.stringify(body), ...options() } as RequestInit)
        .then((response) => {
            if (!response) {
                connectionAlert(new Error("Failed to fetch"), withoutConnectionAlert);
            }
            return response
                .json()
                .then((nextRes) => {
                    const {
                        accessToken: { token, expiresIn },
                        currentUser,
                        refreshToken,
                    } = nextRes;
                    localStorage.setItem("token", token);
                    localStorage.setItem("refreshToken", refreshToken);
                    localStorage.setItem("tokenExpiresIn", moment().add(expiresIn, "seconds").toISOString());
                    localStorage.setItem("currentUser", JSON.stringify(currentUser));
                    localStorage.removeItem("refreshTokenRunning");
                    return { token, currentUser };
                })
                .catch((err) => connectionAlert(err, withoutConnectionAlert));
        })
        .catch((err) => {
            clearLocalStorage();
            setTimeout(() => window.location.reload(), 500);
            connectionAlert(err, withoutConnectionAlert);
        });
}

export function request<T>(
    resource: string,
    params: {},
    reqOptions: RequestOptions & { withStatus: true },
): Promise<ResponseWithStatus<T>>;
export function request(resource: string, params: {}, reqOptions?: RequestOptions): Promise<any>;
export function request(resource: string, params: {}, reqOptions: RequestOptions = {}): Promise<any> {
    const { withoutMiddleware = false, withoutConnectionAlert = false } = reqOptions;
    const currentRequest = () => {
        return fetch(`${url}/${resource}`, { ...options(reqOptions.headers), ...params } as RequestInit)
            .then(async (res) => {
                if (withoutMiddleware) {
                    return res;
                }
                const data = await responseMiddleware(res, reqOptions);
                return reqOptions.withStatus ? { data, status: res.status } : data;
            })
            .catch((err) => connectionAlert(err, withoutConnectionAlert));
    };

    const tokenExpiresIn = localStorage.getItem("tokenExpiresIn");
    if (tokenExpiresIn && moment(tokenExpiresIn) > moment()) {
        return currentRequest();
    }

    if (localStorage.getItem("refreshTokenRunning")) {
        return new Promise((resolve, _reject) => {
            requestsQueue.push(() => resolve(currentRequest()));
        });
    } else {
        localStorage.setItem("refreshTokenRunning", "true");
        return refreshToken(withoutConnectionAlert).then(() => {
            while (requestsQueue.length > 0) {
                const req = requestsQueue.shift();
                req!();
            }
            return currentRequest();
        });
    }
}

export function alertRequest(
    resource: string,
    params: {},
    alertParams?: { successAlerts: boolean; noContentAlerts: boolean },
) {
    return request(resource, params, { shouldAlert: true, alertParams });
}

export function body(params: {}) {
    return params ? { body: JSON.stringify(params) } : {};
}

export function downloadFileHandler(res: Response, fileName: string) {
    if (res.ok) {
        return res.blob().then((blob) => {
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        });
    } else {
        res.text().then((x) => console.error(x));
        alert({ message: "Произошла ошибка", type: "warning" });
    }
}
